import { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_VOICE_MS, pickRecorderMime } from '../utils/voice-message';

// Ghi âm lời nhắn giọng nói khi donate (≤ 15 giây) bằng MediaRecorder.
// Không dùng được (trình duyệt cũ) hoặc bị từ chối quyền micro vẫn phải cho donate
// chữ bình thường — hook chỉ đổi state, không ném lỗi ra ngoài.

export type RecorderState = 'idle' | 'recording' | 'recorded' | 'denied' | 'unsupported';

export interface UseVoiceRecorderResult {
  state: RecorderState;
  elapsedMs: number;
  blob: Blob | null;
  url: string | null;
  mime: string | null;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
}

const typeSupportedFn = () =>
  typeof MediaRecorder !== 'undefined'
    ? (m: string) => MediaRecorder.isTypeSupported(m)
    : undefined;

const isSupported = () =>
  typeof MediaRecorder !== 'undefined' &&
  !!navigator?.mediaDevices?.getUserMedia &&
  !!pickRecorderMime(typeSupportedFn());

export function useVoiceRecorder(): UseVoiceRecorderResult {
  const [state, setState] = useState<RecorderState>(() => (isSupported() ? 'idle' : 'unsupported'));
  const [elapsedMs, setElapsedMs] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [mime, setMime] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const urlRef = useRef<string | null>(null);
  const suppressStopRef = useRef(false);
  const mountedRef = useRef(true);

  const clearTimers = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  };

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const revokeUrl = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setUrl(null);
  };

  const start = useCallback(async () => {
    if (!isSupported()) {
      setState('unsupported');
      return;
    }

    // Xoá bản ghi trước đó ngay khi bắt đầu ghi lại — kể cả khi quyền micro bị từ chối ngay sau đây.
    // Không làm vậy thì "Ghi lại" bị từ chối quyền vẫn giữ nguyên blob/url cũ: khung donate vẫn coi
    // như còn lời nhắn kèm theo (gửi đi một bản ghi người dùng không còn thấy trên UI) — xem M4 final review.
    revokeUrl();
    setBlob(null);
    setMime(null);
    chunksRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      if (mountedRef.current) setState('denied');
      return;
    }

    if (!mountedRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    const chosenMime = pickRecorderMime(typeSupportedFn()) as string;
    streamRef.current = stream;
    setMime(chosenMime);

    const recorder = new MediaRecorder(stream, { mimeType: chosenMime });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      clearTimers();
      stopTracks();
      const finalBlob = new Blob(chunksRef.current, { type: chosenMime });
      chunksRef.current = [];

      if (suppressStopRef.current) {
        suppressStopRef.current = false;
        return;
      }
      if (!mountedRef.current) return;

      const objUrl = URL.createObjectURL(finalBlob);
      urlRef.current = objUrl;
      setBlob(finalBlob);
      setUrl(objUrl);
      setState('recorded');
    };

    startedAtRef.current = Date.now();
    setElapsedMs(0);
    recorder.start();
    setState('recording');

    tickRef.current = setInterval(() => {
      if (mountedRef.current) setElapsedMs(Date.now() - startedAtRef.current);
    }, 200);
    autoStopRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
    }, MAX_VOICE_MS);
  }, []);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    } else {
      clearTimers();
      stopTracks();
    }
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      suppressStopRef.current = true;
      try {
        recorderRef.current.stop();
      } catch {
        suppressStopRef.current = false;
      }
    }
    stopTracks();
    revokeUrl();
    recorderRef.current = null;
    setBlob(null);
    setMime(null);
    setElapsedMs(0);
    setState(isSupported() ? 'idle' : 'unsupported');
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        suppressStopRef.current = true;
        try {
          recorderRef.current.stop();
        } catch {
          // ignore
        }
      }
      stopTracks();
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  return { state, elapsedMs, blob, url, mime, start, stop, reset };
}
